import { motion } from "framer-motion";
import { Star, Shield, Zap, CheckCircle, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { useFirstInteraction } from "@/hooks/use-first-interaction";

const badges = [
  { icon: Shield, label: "Licensed & Insured" },
  { icon: Star, label: "5-Star Rated Service" },
  { icon: Zap, label: "Fast Scheduling" },
  { icon: CheckCircle, label: "Satisfaction Guaranteed" },
];

const stats = [
  { value: "500", label: "Homes Cleaned", suffix: "+" },
  { value: "14", label: "Cities Served", suffix: "+" },
  { value: "100", label: "Satisfaction Focus", suffix: "%" },
];

const HeroSection = () => {
  const { openQuoteForm } = useQuoteForm();
  const formReady = useFirstInteraction();

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <img
        src="/images/hero-home-1280.webp"
        srcSet="/images/hero-home-828.webp 828w, /images/hero-home-1280.webp 1280w, /images/hero-home-1920.webp 1920w"
        sizes="100vw"
        width={1920}
        height={1088}
        alt="JR Pro Cleaning Services team cleaning a Tampa home"
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        loading="eager"
        fetchPriority="high"
        decoding="async"
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,30%,6%/0.6)] via-[hsl(222,30%,8%/0.5)] to-[hsl(215,40%,12%/0.4)]" />
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      {/* Decorative ambient blobs — CSS keyframes (was framer-motion repeat:Infinity,
          which kept the main thread busy and hurt TBT/INP). */}
      <div
        aria-hidden="true"
        className="absolute top-20 right-20 w-96 h-96 rounded-full hero-blob hero-blob-a"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.3), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-40 left-10 w-64 h-64 rounded-full hero-blob hero-blob-b"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.3), transparent 70%)" }}
      />

      <div className="relative z-10 container-max px-4 py-32 md:py-44 lg:py-52 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-primary/15 backdrop-blur-sm border border-primary/30 text-white text-sm font-semibold px-5 py-2 rounded-full mb-10"
              >
                <Sparkles className="h-4 w-4" />
                Tampa's Trusted Cleaning Professionals
              </motion.div>

              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] mb-8 tracking-tight font-bold">
                A Cleaner
                <br />
                <span className="text-gradient-hero">Home</span> Starts
                <br />
                Here
              </h1>
              <p className="text-white/85 text-lg md:text-xl mb-10 leading-relaxed max-w-xl">
                House cleaning, deep cleaning, and commercial cleaning across Tampa Bay —
                detail-driven, reliable, and backed by a team that treats your space like their own.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]"
                >
                  Get Free Estimate
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="text-base gap-2 px-8 py-6 text-lg rounded-xl border-2 border-white/25 text-white bg-white/5 hover:bg-white/15 hover:text-white hover:border-white/40 backdrop-blur-sm"
                >
                  <a href="tel:9785645018"><Phone className="h-5 w-5" /> (978) 564-5018</a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="glass-card rounded-xl px-5 py-3 inline-flex items-center gap-3"
              >
                <Shield className="h-6 w-6 text-secondary" />
                <span className="text-white/90 font-semibold text-sm"><span className="text-gradient-hero font-bold">Fully Insured & Certified</span></span>
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-60 blur-2xl pointer-events-none -z-10"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.35), transparent 70%)" }}
            />
            <div className="relative rounded-2xl overflow-hidden shadow-premium-lg bg-card p-6 border border-border/60">
              <h2 className="font-heading text-2xl text-foreground mb-1 font-bold">Request a Free Estimate</h2>
              <p className="text-muted-foreground text-sm mb-4">Tell us about your space — we'll respond fast.</p>
              {/* The estimate form is a cross-origin LeadConnector iframe that pulls in
                  reCAPTCHA and other heavy resources. It is mounted on first interaction
                  so it stays off the critical path; the placeholder reserves the exact
                  670px height to avoid any layout shift when it swaps in. */}
              {formReady ? (
                <iframe
                  src="https://link.systemic-digital.net/widget/form/3eMUXdWvrrgfTubLLFWN"
                  style={{ width: "100%", height: "670px", border: "none", borderRadius: "6px" }}
                  id="inline-3eMUXdWvrrgfTubLLFWN"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="JR Pro Cleaning Services Form"
                  data-height="670"
                  data-layout-iframe-id="inline-3eMUXdWvrrgfTubLLFWN"
                  data-form-id="3eMUXdWvrrgfTubLLFWN"
                  title="JR Pro Cleaning Services Form"
                />
              ) : (
                <div
                  style={{ width: "100%", height: "670px", borderRadius: "6px" }}
                  className="bg-muted/40 animate-pulse"
                  aria-hidden="true"
                />
              )}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-16 md:mt-20 glass-card rounded-2xl p-6 md:p-8 gradient-border"
        >
          <div className="flex flex-wrap items-center justify-around gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + i * 0.15, duration: 0.5 }}
                className="text-center"
              >
                <div className="font-heading text-3xl md:text-4xl text-white tracking-wide font-bold">
                  {stat.value}<span className="text-gradient-hero">{stat.suffix}</span>
                </div>
                <div className="text-white/75 text-xs font-semibold uppercase tracking-wider mt-1">{stat.label}</div>
              </motion.div>
            ))}
            {badges.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <span className="text-white/80 text-xs font-medium">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="pointer-events-none absolute -bottom-px left-0 right-0 h-16 bg-background leading-none">
        <svg viewBox="0 0 1440 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="block w-full h-full" preserveAspectRatio="none">
          <path d="M0 32C240 64 480 64 720 32C960 0 1200 0 1440 32V64H0V32Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
